/**
 * How the logo sits inside the launcher icon.
 *
 * Android does not show the logo as supplied: it puts the foreground layer behind a mask the
 * launcher chooses, and only the middle two thirds of that layer is guaranteed to survive. A
 * wordmark drawn edge to edge loses its ends; a badge drawn with its own padding ends up looking
 * small next to every other icon on the phone. Neither is visible from the source PNG, so this
 * shows the result under the three masks in common use.
 *
 * The previews are rendered by the build's own code, not redrawn here, so what is on screen is
 * what ships.
 */
import { useEffect, useState } from 'react';

const SHAPES = [
  { key: 'circle', caption: 'Pixel' },
  { key: 'squircle', caption: 'Samsung' },
  { key: 'rounded', caption: 'Xiaomi' },
] as const;

export const ICON_SCALE_DEFAULT = 100;

export function IconFit({ slug, logo, scale, trim, background, onScale, onTrim }: {
  /** An existing client, whose stored logo the server already has. */
  slug?: string | null;
  /** A logo chosen in the creation form, which has to be posted to be rendered. */
  logo?: File | null;
  scale: number;
  trim: boolean;
  background?: string | null;
  onScale: (value: number) => void;
  onTrim: (value: boolean) => void;
}) {
  // Dragging the slider would otherwise spawn a renderer per frame.
  const [settled, setSettled] = useState({ scale, trim });
  useEffect(() => {
    const timer = setTimeout(() => setSettled({ scale, trim }), 220);
    return () => clearTimeout(timer);
  }, [scale, trim]);

  const tint = background && /^#[0-9A-Fa-f]{6}$/.test(background) ? background : null;
  const query = new URLSearchParams({ size: '168', scale: String(settled.scale), trim: String(settled.trim) });
  if (tint) query.set('background', tint);

  const posted = usePostedPreviews(logo ?? null, settled.scale, settled.trim, tint);

  return (
    <div className="icon-fit">
      <div className="icon-fit-shapes">
        {SHAPES.map((shape) => {
          const src = slug
            ? `/api/clients/${slug}/icon-preview?shape=${shape.key}&${query}`
            : posted[shape.key];
          return (
            <figure key={shape.key}>
              {src
                ? <img src={src} alt={`The launcher icon under the ${shape.key} mask`} />
                : <span className={`icon-fit-waiting ${shape.key}`} />}
              <figcaption>{shape.caption}</figcaption>
            </figure>
          );
        })}
      </div>

      <div className="icon-fit-controls">
        <div className="field">
          <label htmlFor={`icon-scale-${slug ?? 'new'}`}>
            Logo size <span className="mono">{scale}%</span>
          </label>
          <input
            id={`icon-scale-${slug ?? 'new'}`}
            name="iconScale"
            type="range"
            min={50}
            max={150}
            step={5}
            value={scale}
            onChange={(event) => onScale(Number(event.target.value))}
          />
          <span className="hint">100% is the default fit.</span>
        </div>

        <label className="check">
          <input type="checkbox" name="iconTrim" checked={trim}
                 onChange={(event) => onTrim(event.target.checked)} />
          <span>
            <strong>Trim the transparent edges first</strong>
            <em>For artwork on a larger canvas than it fills.</em>
          </span>
        </label>

        {scale !== ICON_SCALE_DEFAULT && (
          <button type="button" className="btn quiet small" onClick={() => onScale(ICON_SCALE_DEFAULT)}>
            Back to 100%
          </button>
        )}
      </div>
    </div>
  );
}


type Previews = Partial<Record<(typeof SHAPES)[number]['key'], string>>;

/** Renders a not-yet-saved logo server side, and keeps the object URLs from leaking. */
function usePostedPreviews(logo: File | null, scale: number, trim: boolean, background: string | null): Previews {
  const [previews, setPreviews] = useState<Previews>({});

  useEffect(() => {
    if (!logo) {
      setPreviews({});
      return;
    }
    let live = true;
    const urls: string[] = [];

    void Promise.all(SHAPES.map(async (shape) => {
      const body = new FormData();
      body.set('logo', logo);
      body.set('shape', shape.key);
      body.set('size', '168');
      body.set('scale', String(scale));
      body.set('trim', String(trim));
      if (background) body.set('background', background);
      const response = await fetch('/api/icon-preview', { method: 'POST', body });
      if (!response.ok) return [shape.key, null] as const;
      const url = URL.createObjectURL(await response.blob());
      urls.push(url);
      return [shape.key, url] as const;
    })).then((pairs) => {
      if (!live) {
        urls.forEach(URL.revokeObjectURL);
        return;
      }
      setPreviews(Object.fromEntries(pairs.filter(([, url]) => url)) as Previews);
    }).catch(() => { if (live) setPreviews({}); });

    return () => {
      live = false;
      urls.forEach(URL.revokeObjectURL);
    };
  }, [logo, scale, trim, background]);

  return previews;
}
